export interface CaseStudy {
  id: string;
  sector: string;
  title: string;
  client: string;
  location?: string;
  year?: string;
  summary: string;
  challenge: string;
  approach: string[];
  outcomes: string[];
  impactMetrics: string[];
  tags: string[];
}

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: "multilingual-transcription-platform",
    sector: "AI Transformation / Content Operations",
    title: "Private Multilingual AI Transcription & Translation Platform",
    client: "Multilingual content operations organisation",
    location: "UK, France, Switzerland & Canada",
    year: "2026",
    summary:
      "The client wanted to replace labour-intensive transcription and multilingual translation workflows with a private AI platform that could process long-form audio and documents quickly, preserve specialist organisational terminology, support human review, and serve distributed teams across four countries.",
    challenge:
      "Teams were spending hours manually transcribing long-form recordings, while multilingual translation depended heavily on available human capacity. Generic AI tools also struggled with specialist names, terminology, and contextual references. The client needed faster processing without sacrificing privacy, reviewability, terminology consistency, or operational control.",
    approach: [
      "Designed and built the platform end to end, combining AI transcription, speaker diarisation, multilingual translation, human review, editing, and export workflows.",
      "Privately deployed faster-whisper, pyannote, TowerInstruct and MADLAD models on controlled GPU infrastructure rather than sending core content through third-party AI APIs.",
      "Built persistent English and language-specific glossaries so specialist terminology and reviewer corrections could improve future transcription and translation jobs.",
      "Implemented role-based access, per-language permissions, private object storage, signed authentication, and controlled file access.",
      "Engineered on-demand GPU orchestration with job queues, automatic provisioning, idle shutdown, and workload-level cost attribution.",
      "Iterated the infrastructure across Azure, Vultr, RunPod and Scaleway to improve GPU availability, reliability, performance, and operating cost.",
      "Built administration, analytics, observability, user management, service-status controls, and Mattermost operational notifications.",
      "Optimised translation inference for a roughly two-hour workload from more than 20 minutes to under 2 minutes, materially reducing both processing time and GPU cost.",
    ],
    outcomes: [
      "Replaced a heavily manual workflow with a production AI platform now used by distributed teams across the UK, France, Switzerland, and Canada.",
      "Processed 127 completed transcriptions, 43.1 hours of audio, and more than 505,000 words.",
      "Generated an estimated ~194 hours of manual typing effort saved.",
      "Supported multilingual translation across 20+ languages, including European, Asian, Middle Eastern, and African languages.",
      "Demonstrated approximately 3-minute transcription of 230 minutes (3h50m) of source audio in a documented workload.",
      "Gave administrators visibility into AI usage, GPU costs, job activity, users, and runtime service status.",
      "Created a reusable organisational knowledge layer through persistent terminology and reviewer feedback.",
    ],
    impactMetrics: [
      "~194 hours of manual effort saved",
      "505K+ words transcribed across 43.1 hours of audio",
      "127 completed transcriptions",
      "20+ translation languages",
      "Operational use across 4 countries",
      "3h50m of audio processed in ~3 minutes",
      "~2-hour translation reduced from 20+ minutes to under 2 minutes",
      "Private, cost-controlled GPU inference",
      "Built-in analytics, observability, RBAC, and operational controls",
    ],
    tags: [
      "AI Transformation",
      "Speech AI",
      "Multilingual AI",
      "Private AI Infrastructure",
      "GPU Orchestration",
      "Human-in-the-Loop",
      "Observability",
      "FinOps",
    ],
  },
  {
    id: "fraud-observability",
    sector: "Financial Services",
    title: "Fraud & Risk Observability for a Digital Bank",
    client: "Microfinance / digital banking institution",
    location: "Nigeria",
    year: "2023",
    summary:
      "Multiple fraud and credit models were in production, but teams had limited visibility into how they behaved on live traffic.",
    challenge:
      "Risk and compliance teams struggled to explain decisions, making regulator conversations and further automation painful.",
    approach: [
      "Mapped AS-IS fraud and credit workflows, including data flows and escalation paths.",
      "Designed an observability-first reference architecture for fraud scoring and rule engines.",
      "Implemented unified telemetry across scoring APIs, rules, and manual overrides.",
      "Introduced explainability views to surface drivers behind model outputs.",
    ],
    outcomes: [
      "Improved transparency into fraud decisions, reducing investigation time.",
      "Enabled safer rollout of new models with monitored pilots and guardrails.",
      "Provided narrative-friendly views for risk and compliance stakeholders.",
    ],
    impactMetrics: [
      "Clearer fraud-decision investigation workflow",
      "Safer model rollout with monitored pilots",
      "Traceability across scoring, rules, and overrides",
    ],
    tags: [
      "Fraud Detection",
      "Observability",
      "FinTech",
      "Risk Management",
      "Explainability",
    ],
  },
  {
    id: "legal-decision-support",
    sector: "Legal Tech",
    title: "Legal Decision Support with Case Outcome Prediction",
    client: "Legal research / decision support platform",
    location: "UK",
    year: "2024",
    summary:
      "The client wanted to support legal practitioners with outcome prediction, similarity search, and explainable reasoning over case law.",
    challenge:
      "Legal teams needed reliable predictions and similarity suggestions, but also clear traceability to legal principles and precedents.",
    approach: [
      "Built a neural and neuro-symbolic pipeline combining LegalBERT, neural networks, and knowledge graphs.",
      "Integrated a Neo4j knowledge graph for case relationships, citations, and legal concepts.",
      "Implemented explainability techniques to highlight factors driving each prediction.",
      "Surfaced similar cases and relevant excerpts alongside each prediction.",
    ],
    outcomes: [
      "Gave practitioners an at-a-glance view of likely outcomes with supporting context.",
      "Increased confidence in AI suggestions via transparent reasoning paths.",
      "Created a foundation for future RAG and conversational exploration over legal corpora.",
    ],
    impactMetrics: [
      "Explainable outcome-prediction workflow",
      "Faster access to relevant case-law context",
      "Traceable reasoning paths for practitioners",
    ],
    tags: ["Legal Tech", "Knowledge Graphs", "NLP", "Explainability", "Neo4j"],
  },
  {
    id: "datalens-rag",
    sector: "Cross-industry / Research",
    title: "DataLens AI — RAG Assistant for Multifile Datasets",
    client: "Data-heavy teams (research, legal, operations)",
    summary:
      "Teams were overwhelmed by scattered CSVs, PDFs, and documents, making consistent answers hard to obtain.",
    challenge:
      "Analysts and non-technical stakeholders spent significant time manually searching across multiple sources.",
    approach: [
      "Designed and implemented a RAG-based assistant ingesting CSV, Excel, PDF, and DOCX files.",
      "Used vector search and structured indexing to return source-grounded answers.",
      "Wrapped the system in a clean chat interface and exposed API endpoints.",
      "Ensured responses were strictly grounded in uploaded data, with citations to sources.",
    ],
    outcomes: [
      "Reduced time-to-insight by centralising retrieval and reasoning.",
      "Enabled non-technical users to ask complex questions in natural language.",
      "Improved trust by keeping answers tied to verifiable documents.",
    ],
    impactMetrics: [
      "Source-grounded answers across mixed files",
      "Natural-language access to complex datasets",
      "Citations surfaced with each answer",
    ],
    tags: [
      "RAG",
      "Vector Search",
      "Document Processing",
      "LLM",
      "Data Analysis",
    ],
  },
  {
    id: "sovereign-robotics-ops",
    sector: "Robotics R&D",
    title: "Robotics Operations Governance — A Simulation Prototype",
    client: "Independent R&D prototype — LabLab.ai hackathon",
    year: "2026",
    summary:
      "Autonomous robots increasingly act on AI-generated plans with no runtime check before execution — no audit trail, no policy layer to block an unsafe action in real time.",
    challenge:
      "When a robot makes a wrong decision, operators can't trace why. Robots act on LLM outputs without validation, and robotics teams lack tools to enforce compliance, reproduce failures, or prove safety to regulators.",
    approach: [
      "Built a governance layer that sits between an LLM planner and robot execution, intercepting every proposed action before it runs.",
      "Real-time policy checks against operational limits — human proximity, speed thresholds, payload limits, and geofencing.",
      "Combined LLM-proposed actions with a symbolic validation layer, so the AI proposes but a deterministic policy engine decides.",
      "Built and demonstrated in simulation (Webots / Isaac Sim), with a live operator dashboard showing the full decision trail.",
    ],
    outcomes: [
      "Every proposed robot action resolves to one of four governed states — SAFE, SLOW, STOP, or REPLAN — before execution.",
      "Full decision trail recorded per action: sensor input, proposed action, policy check, and result.",
      "Demonstrated the same pattern NOVTIA applies elsewhere: a checkpoint the AI has to pass, not a log reviewed after the fact.",
    ],
    impactMetrics: [
      "4 governed decision states (SAFE / SLOW / STOP / REPLAN)",
      "Demonstrated in simulation; not a production deployment",
    ],
    tags: [
      "Robotics",
      "Runtime Governance",
      "Simulation",
      "Policy-as-Code",
      "Neuro-Symbolic AI",
    ],
  },
];
